import { API, auth } from "./api";

function getSessionId() {
  let sid = sessionStorage.getItem("wve_sid");
  if (!sid) {
    sid = window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem("wve_sid", sid);
  }
  return sid;
}

function detectSource() {
  const p = new URLSearchParams(window.location.search).get("src");
  if (p) return p.slice(0, 40);
  const ref = document.referrer;
  if (!ref) return "direct";
  try {
    const host = new URL(ref).hostname;
    if (host === window.location.hostname) return "internal";
    if (/whatsapp/.test(host) || host === "wa.me") return "whatsapp";
    if (/google\./.test(host)) return "google";
    if (/instagram/.test(host)) return "instagram";
    if (/facebook|fb\./.test(host)) return "facebook";
    return host.slice(0, 40);
  } catch {
    return "other";
  }
}

function sendEvents(url, sid, source, events, useBeacon) {
  const body = JSON.stringify({ session_id: sid, source, events });
  if (useBeacon && navigator.sendBeacon) {
    navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
  } else {
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  }
}

function createSectionTimer() {
  const pending = {};
  const visibleSince = {};

  const settle = () => {
    const now = performance.now();
    Object.keys(visibleSince).forEach((name) => {
      pending[name] = (pending[name] || 0) + (now - visibleSince[name]) / 1000;
      visibleSince[name] = now;
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      const now = performance.now();
      entries.forEach((en) => {
        const name = en.target.getAttribute("data-track-section");
        if (!name) return;
        if (en.isIntersecting) {
          visibleSince[name] = now;
        } else if (visibleSince[name] != null) {
          pending[name] = (pending[name] || 0) + (now - visibleSince[name]) / 1000;
          delete visibleSince[name];
        }
      });
    },
    { threshold: 0.35 }
  );

  const drain = () => {
    settle();
    const events = [];
    Object.entries(pending).forEach(([name, secs]) => {
      if (secs >= 1) {
        events.push({ type: "section_time", name, seconds: Math.round(secs * 10) / 10 });
        pending[name] = 0;
      }
    });
    return events;
  };

  return { observer, drain };
}

function createScrollTracker(queue) {
  const fired = new Set();
  const onScroll = () => {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    if (max <= 0) return;
    const pct = (window.scrollY / max) * 100;
    [25, 50, 75, 100].forEach((m) => {
      if (pct >= m - 1 && !fired.has(m)) {
        fired.add(m);
        queue.push({ type: "scroll_depth", name: String(m) });
      }
    });
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  return () => window.removeEventListener("scroll", onScroll);
}

function createDwellTracker() {
  const pending = {};
  let current = null;
  let since = 0;

  const onOver = (e) => {
    const el = e.target.closest?.("[data-track-dwell]");
    const name = el ? el.getAttribute("data-track-dwell") : null;
    if (name === current) return;
    const now = performance.now();
    if (current) pending[current] = (pending[current] || 0) + (now - since) / 1000;
    current = name;
    since = now;
  };

  const drain = () => {
    const now = performance.now();
    if (current) {
      pending[current] = (pending[current] || 0) + (now - since) / 1000;
      since = now;
    }
    const events = [];
    Object.entries(pending).forEach(([name, secs]) => {
      if (secs >= 1) {
        events.push({ type: "package_dwell", name, seconds: Math.round(secs * 10) / 10 });
        pending[name] = 0;
      }
    });
    return events;
  };

  document.addEventListener("pointerover", onOver, true);
  return { drain, cleanup: () => document.removeEventListener("pointerover", onOver, true) };
}

function createVideoTracker(queue) {
  const frames = [];

  const setupTimer = setTimeout(() => {
    document.querySelectorAll("iframe[data-track-video]").forEach((f) => {
      if (!f.contentWindow) return;
      frames.push({
        win: f.contentWindow,
        name: f.getAttribute("data-track-video"),
        since: null,
        played: false,
        pending: 0,
      });
      try {
        f.contentWindow.postMessage(JSON.stringify({ event: "listening", id: 1, channel: "widget" }), "*");
      } catch {}
    });
  }, 1500);

  const onMsg = (e) => {
    if (typeof e.origin !== "string" || !e.origin.includes("youtube")) return;
    let d;
    try {
      d = JSON.parse(e.data);
    } catch {
      return;
    }
    const state =
      d.event === "onStateChange"
        ? d.info
        : d.event === "infoDelivery" && d.info
        ? d.info.playerState
        : undefined;
    if (typeof state !== "number") return;
    const fr = frames.find((x) => x.win === e.source);
    if (!fr) return;
    const now = performance.now();
    if (state === 1) {
      if (!fr.played) {
        fr.played = true;
        queue.push({ type: "click", name: `video-play-${fr.name}` });
      }
      if (fr.since == null) fr.since = now;
    } else if (fr.since != null) {
      fr.pending += (now - fr.since) / 1000;
      fr.since = null;
    }
  };

  const drain = () => {
    const now = performance.now();
    const events = [];
    frames.forEach((fr) => {
      if (fr.since != null) {
        fr.pending += (now - fr.since) / 1000;
        fr.since = now;
      }
      if (fr.pending >= 1) {
        events.push({ type: "video_watch", name: fr.name, seconds: Math.round(fr.pending * 10) / 10 });
        fr.pending = 0;
      }
    });
    return events;
  };

  window.addEventListener("message", onMsg);
  return {
    drain,
    cleanup: () => {
      clearTimeout(setupTimer);
      window.removeEventListener("message", onMsg);
    },
  };
}

function isPreviewMode() {
  if (new URLSearchParams(window.location.search).has("preview")) {
    sessionStorage.setItem("wve_preview", "1");
    return true;
  }
  return sessionStorage.getItem("wve_preview") === "1";
}

let activeQueue = null;

export function trackEvent(type, name, seconds = 0) {
  if (activeQueue) activeQueue.push({ type, name, seconds });
}

let sidePageInit = false;
export function initSidePageTracking(pageName) {
  const slug = new URLSearchParams(window.location.search).get("q");
  if (!slug || sidePageInit || auth.isAdminHint() || isPreviewMode()) return () => {};
  sidePageInit = true;
  const sid = getSessionId();
  const url = `${API}/public/quotes/${slug}/track`;
  const started = performance.now();
  sendEvents(url, sid, "internal", [{ type: "click", name: `${pageName}-page-viewed` }], false);
  const onLeave = () => {
    const secs = Math.round(((performance.now() - started) / 1000) * 10) / 10;
    if (secs >= 1) {
      sendEvents(url, sid, "internal", [{ type: "section_time", name: `${pageName}-page`, seconds: secs }], true);
    }
  };
  window.addEventListener("pagehide", onLeave);
  return () => window.removeEventListener("pagehide", onLeave);
}

export function initQuoteTracking(slug) {
  if (auth.isAdminHint() || isPreviewMode()) return () => {};
  const sid = getSessionId();
  const source = detectSource();
  const url = `${API}/public/quotes/${slug}/track`;
  const queue = [{ type: "view", name: "page" }];
  activeQueue = queue;
  const { observer, drain } = createSectionTimer();
  const scrollCleanup = createScrollTracker(queue);
  const dwell = createDwellTracker();
  const video = createVideoTracker(queue);
  let lastSent = 0;

  const flush = (useBeacon = false) => {
    queue.push(...drain(), ...dwell.drain(), ...video.drain());
    const now = Date.now();
    if (!queue.length) {
      if (!useBeacon && document.visibilityState === "visible" && now - lastSent >= 25000) {
        lastSent = now;
        sendEvents(url, sid, source, [{ type: "heartbeat", name: "hb" }], false);
      }
      return;
    }
    lastSent = now;
    sendEvents(url, sid, source, queue.splice(0), useBeacon);
  };

  const observeTimer = setTimeout(() => {
    document.querySelectorAll("[data-track-section]").forEach((el) => observer.observe(el));
  }, 500);

  const onClick = (e) => {
    const el = e.target.closest("[data-track-click]");
    if (el) queue.push({ type: "click", name: el.getAttribute("data-track-click") });
  };
  const onVis = () => {
    if (document.visibilityState === "hidden") flush(true);
  };
  const onHide = () => flush(true);

  document.addEventListener("click", onClick, true);
  document.addEventListener("visibilitychange", onVis);
  window.addEventListener("pagehide", onHide);
  const interval = setInterval(() => flush(false), 10000);
  const firstFlush = setTimeout(() => flush(false), 1500);

  return () => {
    flush(true);
    activeQueue = null;
    clearTimeout(observeTimer);
    clearTimeout(firstFlush);
    clearInterval(interval);
    observer.disconnect();
    scrollCleanup();
    dwell.cleanup();
    video.cleanup();
    document.removeEventListener("click", onClick, true);
    document.removeEventListener("visibilitychange", onVis);
    window.removeEventListener("pagehide", onHide);
  };
}
